import { Booking, BookingType } from '@customTypes/booking.interface'
import { Subscription } from '@customTypes/subscription.interface'
import React, { FC } from 'react'
import {
    CardType,
    Container,
    ContentWrapper,
    DataText,
    HeaderWrapper,
    LotName,
    RepeatCircle,
    RepeatIcon
} from './BookingCard.style'
import { useTranslation } from 'react-i18next'
import { getDurationStatus, getSubscriptionDurationStatus } from '@utils/getDurationStatus.utils'
import Badge from '@components/Badge/Badge.component'
import { DateTime } from 'luxon'
import { useHistory } from 'react-router-dom'
import { bookingDetailsRoute } from '@views/BookingDetails/route.const'
import { subscriptionDetailsRoute } from '@views/SubscriptionDetails/route.const'

interface Props {
    type: 'booking' | 'subscription'
    lotName: string
    startDate: string
    endDate: string
    plates: string[]
    booking?: Booking
    subscription?: Subscription
}

const BookingCard: FC<Props> = ({ type, startDate, endDate, plates, lotName, booking, subscription }) => {
    const { t } = useTranslation()
    const history = useHistory()
    const status =
        type === 'booking' ? getDurationStatus(startDate, endDate) : getSubscriptionDurationStatus(startDate, endDate)

    const start: DateTime = DateTime.fromISO(startDate)
    const end: DateTime = DateTime.fromISO(endDate)

    const onClick = () => {
        if (type === 'booking') {
            history.push(bookingDetailsRoute(booking?.booking_ref as string))
        } else {
            history.push(subscriptionDetailsRoute(subscription?.id as string))
        }
    }

    return (
        <Container onClick={onClick}>
            <HeaderWrapper>
                <CardType>{t(`home.${type === 'booking' ? 'reservation' : 'subscription'}`)}</CardType>
                <Badge variant={status.variant} text={t(status.tKey)} />
            </HeaderWrapper>
            <ContentWrapper>
                <div>
                    <LotName>{lotName}</LotName>
                    <DataText>
                        {type === 'booking' ? (
                            <>
                                {/* daily, nightly bookings */}
                                {booking?.booking_type !== BookingType.HOURLY && (
                                    <>
                                        {t('home.period')}: {start.toFormat('dd.MM.yyyy')} -{' '}
                                        {end.toFormat('dd.MM.yyyy')}
                                    </>
                                )}

                                {/* hourly booking */}
                                {booking?.booking_type === BookingType.HOURLY && <>{start.toFormat('dd.MM.yyyy')}</>}
                            </>
                        ) : (
                            <>
                                {/* subscription */}
                                {t('home.since')} {start.toFormat('dd.MM.yyyy')}
                            </>
                        )}
                    </DataText>

                    {/* if nightly or hourly booking - show time */}
                    {(booking?.booking_type === BookingType.NIGHTLY ||
                        booking?.booking_type === BookingType.HOURLY) && (
                        <DataText>
                            <span>
                                {start.toFormat('HH:mm')} - {end.toFormat('HH:mm')} Uhr
                            </span>
                        </DataText>
                    )}

                    <DataText>
                        <span>{plates.map((plate) => plate)}</span>
                    </DataText>
                </div>
                {type === 'subscription' && (
                    <RepeatCircle $type={status.variant}>
                        <RepeatIcon className="wemolo-refresh" aria-hidden="true" />
                    </RepeatCircle>
                )}
                {type === 'booking' && booking?.booking_type === BookingType.NIGHTLY && (
                    <RepeatCircle $type={status.variant}>
                        <RepeatIcon className="wemolo-nightparking" aria-hidden="true" />
                    </RepeatCircle>
                )}
            </ContentWrapper>
        </Container>
    )
}

export default BookingCard
